import React, { useEffect, useState, useRef, useMemo } from "react";
import { Button } from "react-bootstrap";
import styles from "../../../_css/AddRoleplay.module.css"

const Scenario = ({
    handleNext,
    handleBack,
    step = 2,
    previousSelections = {},
    isLoading 
}) => {
    const [inputs, setInputs] = useState({
        conversationContext: "",
        personInvolved: "",
        previousEvents: "",
        challengingAspects: "",
        potentialConsequences: ""
    });

    const textareaRefs = {
        conversationContext: useRef(null),
        personInvolved: useRef(null),
        previousEvents: useRef(null),
        challengingAspects: useRef(null),
        potentialConsequences: useRef(null)
    };

    // Initialize Data
    useEffect(() => {
        const savedData = previousSelections?.[`step${step}`];
        if (savedData) {
            const saved = typeof savedData === 'string' ? JSON.parse(savedData) : savedData;
            setInputs({
                conversationContext: saved.conversationContext || "",
                personInvolved: saved.personInvolved || "",
                previousEvents: saved.previousEvents || "",
                challengingAspects: saved.challengingAspects || "",
                potentialConsequences: saved.potentialConsequences || ""
            });
        }
    }, [step, previousSelections]);

    const countWords = (text) => text.trim().split(/\s+/).filter(Boolean).length;

    // Memoized counts to avoid re-renders
    const totalWords = useMemo(() => Object.values(inputs).reduce((acc, curr) => acc + countWords(curr), 0), [inputs]);
    const combinedText = useMemo(() => Object.values(inputs).map(v => v.trim()).filter(Boolean).join(". "), [inputs]);

    const handleInputChange = (field, value) => {
        setInputs(prev => ({ ...prev, [field]: value }));
        // Auto-resize logic
        const ref = textareaRefs[field].current;
        if (ref) {
            ref.style.height = 'auto';
            ref.style.height = `${ref.scrollHeight}px`;
        }
    };

    const onNextClick = () => {
        // Construct the final payload directly
        const finalData = {
            ...inputs,
            scenario_description: combinedText, // Use the combined inputs for the API payload
            short_description: "Custom Scenario",
            isAIGenerated: false
        };

        // Pass data to parent's handleNext which manages the API call and Loader
        handleNext(finalData);
    };

    const renderQuestion = (id, labelText, placeholderText, field) => (
        <div className={styles.formGroup}>
            <label className={styles.label}>
                {id}. {labelText} {id === 1 && <span className={styles.required}>*</span>}
            </label>
            <textarea
                ref={textareaRefs[field]}
                className={`${styles.textArea}`}
                placeholder={placeholderText}
                value={inputs[field]}
                onChange={(e) => handleInputChange(field, e.target.value)}
                rows={1}
            />
        </div>
    );

    return (
        <div className={styles.stepCard}>
            {/* Header */}
            <div className={styles.headerContainer}>
                <div>
                    <h4 className={styles.title}>Describe Your Scenario</h4>
                    <p className={styles.subHeader}>Describe the situation and context for this conversation. We've already captured the basic roles in step 1 — now expand with details that bring the scenario to life. Include what happened earlier, how the other person behaves, and what makes it challenging.</p>
                </div>
                <div className={styles.wordCountBadge}>
                    <span>Words: {totalWords}/50</span>
                </div>
            </div>

            {/* Questions Form */}
            {renderQuestion(1, "What's the main issue in this conversation?", "E.g. Customer calls angrily about a rejected claim; it's their third call; I'm not familiar with the case.", "conversationContext")}
            {renderQuestion(2, "Describe the other person's behaviour in this situation.", "E.g. Very upset customer, raising their voice, insists on immediate resolution.", "personInvolved")}
            {renderQuestion(3, "What's happened before this conversation?", "E.g. This is the third contact; they already spoke with my colleague", "previousEvents")}
            {renderQuestion(4, "What's challenging for you in this conversation?", "E.g. I feel under pressure and don't know the case well.", "challengingAspects")}
            {renderQuestion(5, "What could happen if this conversation goes badly?", "E.g. The customer may escalate to my manager or file a formal complaint.", "potentialConsequences")}

            {/* Action Buttons */}
            <div className={styles.buttonGroup}>
                <Button
                    className={styles.backButton}
                    onClick={handleBack}
                    disabled={isLoading} // Disable back while loading
                >
                    Back
                </Button>
                <Button
                    className={styles.nextButton}
                    disabled={!inputs.conversationContext.trim() || isLoading}
                    onClick={onNextClick}
                >
                    {isLoading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Saving...
                        </>
                    ) : (
                        "Continue"
                    )}
                </Button>
            </div>
        </div>
    );
};

export default Scenario;